import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { SafeText } from "@/shared/components/SafeText";
import { sanitizeDisplayText } from "@/shared/lib/safe-text";
import { LocationChecklist } from "./LocationChecklist";
import { ORGANIZATION_LOCATIONS_MAX } from "./location-limits";
import type { MemberLocation } from "./use-member-locations";

/** From this many active locations on, a name filter is offered. */
const FILTER_MIN_LOCATIONS = 8;

interface MemberLocationsRemoveListProps {
  readonly locations: readonly MemberLocation[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  /** Re-reads the list after a failed read. */
  readonly onRetry: () => void;
  /** Ticked = to remove. */
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (id: string, name: string) => void;
  /** Ids that cannot be ticked (a write in flight). */
  readonly disabledIds: readonly string[];
  readonly testIdPrefix: string;
}

/**
 * What the member holds today: the active locations as a checklist to remove
 * from (filterable by name once the list is long, and capped at the API's
 * per-write limit like the add side), and the deactivated ones apart (adding
 * one again reactivates it).
 */
export function MemberLocationsRemoveList(props: MemberLocationsRemoveListProps) {
  const { t } = useTranslation();
  if (props.isLoading) {
    return (
      <p
        data-testid={`${props.testIdPrefix}-current-loading`}
        className="text-sm text-muted-foreground"
      >
        {t("organizationAccess.manageLocations.currentLoading")}
      </p>
    );
  }
  if (props.isError) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <p data-testid={`${props.testIdPrefix}-current-error`} className="text-sm text-destructive">
          {t("organizationAccess.manageLocations.currentError")}
        </p>
        <Button
          data-testid={`${props.testIdPrefix}-current-retry`}
          type="button"
          variant="outline"
          size="sm"
          onClick={props.onRetry}
        >
          {t("organizationAccess.retry")}
        </Button>
      </div>
    );
  }
  return <LoadedList {...props} />;
}

function LoadedList({
  locations,
  selected,
  onToggle,
  disabledIds,
  testIdPrefix,
}: MemberLocationsRemoveListProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("");
  const active = locations.filter((l) => l.active);
  const inactive = locations.filter((l) => !l.active);
  const term = filter.trim().toLocaleLowerCase();
  const shown = term
    ? active.filter((l) => sanitizeDisplayText(l.name).toLocaleLowerCase().includes(term))
    : active;
  const capReached = selected.size >= ORGANIZATION_LOCATIONS_MAX;
  const blocked = capReached
    ? [...disabledIds, ...active.filter((l) => !selected.has(l.id)).map((l) => l.id)]
    : disabledIds;

  return (
    <div className="space-y-2">
      {active.length === 0 ? (
        <p data-testid={`${testIdPrefix}-current-none`} className="text-sm text-muted-foreground">
          {t("organizationAccess.manageLocations.currentNone")}
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {t("organizationAccess.manageLocations.removeHint")}
          </p>
          {active.length >= FILTER_MIN_LOCATIONS && (
            <Input
              data-testid={`${testIdPrefix}-remove-filter`}
              type="search"
              value={filter}
              placeholder={t("organizationAccess.manageLocations.filterPlaceholder")}
              aria-label={t("organizationAccess.manageLocations.filterLabel")}
              onChange={(event) => setFilter(event.target.value)}
            />
          )}
          {shown.length === 0 ? (
            <p
              data-testid={`${testIdPrefix}-remove-no-matches`}
              className="text-sm text-muted-foreground"
            >
              {t("organizationAccess.locations.noMatches")}
            </p>
          ) : (
            <LocationChecklist
              locations={shown}
              selected={selected}
              onToggle={onToggle}
              disabledIds={blocked}
              testIdPrefix={`${testIdPrefix}-remove-location`}
              label={t("organizationAccess.manageLocations.currentTitle")}
            />
          )}
          {capReached && (
            <p data-testid={`${testIdPrefix}-remove-cap`} className="text-xs text-muted-foreground">
              {t("organizationAccess.locations.capReached", { max: ORGANIZATION_LOCATIONS_MAX })}
            </p>
          )}
        </>
      )}
      {inactive.length > 0 && (
        <div data-testid={`${testIdPrefix}-inactive-locations`} className="space-y-1">
          <p className="text-xs text-muted-foreground">
            {t("organizationAccess.manageLocations.inactiveNote")}
          </p>
          <ul className="space-y-0.5 text-sm text-muted-foreground">
            {inactive.map((l) => (
              <li key={l.id} className="break-words">
                <SafeText fallback={l.id}>{l.name}</SafeText>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
